/**
 * Workflows Hub — Goal Picker (Hinglish-friendly, with onboarding strip)
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Youtube, FileText, Search, Share2, Megaphone, Facebook,
  Palette, GraduationCap, Sparkles, ArrowRight, Loader2,
  Wand2, Layers, Rocket, Play, X,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "./workflows.css";

const ICONS = {
  Sparkles, Youtube, FileText, Search, Share2, Megaphone,
  Facebook, Palette, GraduationCap,
};

const API = process.env.REACT_APP_BACKEND_URL || '';

const PREVIEWS = {
  youtube: ["📊 Trending topics", "📝 Optimized title (A/B)", "🎬 Full script", "🖼 Thumbnail concepts", "🏷 Tags + hashtags", "♻ Shorts + social repurpose"],
  content: ["🔍 Topic research", "🔑 Keyword cluster", "📄 1000-word SEO article", "✅ Quality check", "💡 5 headline variants", "♻ Repurpose pack"],
  seo: ["🔬 Site audit", "🎯 Keyword strategy", "🥊 Competitor gaps", "🔗 Backlink profile", "📈 Rank tracker setup", "🗓 30/60/90-day plan"],
  social: ["💬 3 captions (LLM)", "#️⃣ Hashtag pack", "🎨 Post design brief", "⏰ Best-time schedule"],
  google_ads: ["🛒 Buying-intent keywords", "📦 Ad groups", "📝 Ad copy", "🎯 Landing CRO", "💰 Budget guidance", "📤 Campaign export"],
  meta_ads: ["👥 Audience + angle", "📝 Hook + Body + CTA", "🎨 Creative concepts", "🔀 A/B matrix", "🚀 Launch package"],
  design: ["🎨 Brand kit options", "🖼 Asset concepts", "✂️ Background clean-up", "📦 Multi-size export"],
  education: ["📚 Topic research", "📋 Lesson outline", "🎞 Slide deck", "🎤 Auto SRT captions", "📄 Printable PDF"],
  demo: ["💡 Topic angle", "📋 Quick outline", "💬 3 captions"],
};

export default function WorkflowsHub() {
  const navigate = useNavigate();
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    document.title = "Workflows — Pick a goal, get a finished result | MarkeeMix";
    axios.get(`${API}/api/workflows/tracks`)
      .then((r) => setTracks(r.data.tracks || []))
      .catch(() => setTracks([]))
      .finally(() => setLoading(false));
  }, []);

  const handleCardClick = (track) => {
    if (track.status !== "live") return;
    navigate(`/workflows/${track.key}`);
  };

  const featured = tracks.find((t) => t.key === "youtube") || tracks[0];
  const rest = tracks.filter((t) => t.key !== (featured?.key));

  return (
    <>
      <Header />
      <div className="wf-hub" data-testid="workflows-hub">
        <div className="wf-hub-inner">
          <span className="wf-eyebrow" data-testid="workflows-eyebrow">Workflows • Aaj kya banayein?</span>
          <h1 className="wf-hub-title" data-testid="workflows-title">
            Goal pick karo. <em>Finished result paao.</em>
          </h1>
          <p className="wf-hub-sub" data-testid="workflows-subtitle">
            Sirf ek line do — humara AI engine sahi tools ko sahi order mein chalata hai aur
            poora finished package ek hi page par editable form mein deta hai.
            <strong> No more tool-hopping.</strong>
          </p>

          {/* HOW IT WORKS STRIP */}
          <div className="wf-howitworks" data-testid="workflows-how">
            {[
              { icon: Wand2, t: "01 · Ek goal pick karo", d: "Niche se 9 goals mein se choose karo — YouTube, Blog, SEO, Ads, Social, Design ya Lesson." },
              { icon: Sparkles, t: "02 · Ek line type karo (ya AI fill kare)", d: "Apna idea ek line mein. ‘✨ AI se fill karo’ button bhi hai — woh sab fields khud bhar dega." },
              { icon: Rocket, t: "03 · Finished package paao", d: "5–8 tools background mein chalte hain. Output ek page par — Copy / Edit / Regenerate ready." },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="wf-how-step" data-testid={`workflows-how-step-${i}`}>
                  <div className="wf-how-icon"><Icon size={18} /></div>
                  <div>
                    <div className="wf-how-title">{s.t}</div>
                    <div className="wf-how-desc">{s.d}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {loading ? (
            <div style={{ marginTop: 64, color: "#5a4d6f", display: "flex", alignItems: "center", gap: 8 }}>
              <Loader2 className="animate-spin" size={18} /> Loading goals…
            </div>
          ) : (
            <>
              {/* FEATURED CARD */}
              {featured && (
                <div
                  className="wf-featured"
                  data-testid={`workflow-featured-${featured.key}`}
                  onClick={() => handleCardClick(featured)}
                >
                  <div className="wf-featured-left">
                    <span className="wf-pill live" style={{ marginBottom: 14 }}>✨ Most popular</span>
                    <h2 className="wf-featured-title">{featured.name}</h2>
                    <p className="wf-featured-tagline">{featured.tagline}</p>
                    <div className="wf-featured-previews">
                      {(PREVIEWS[featured.key] || []).map((p, i) => (
                        <span key={i} className="wf-preview-chip">{p}</span>
                      ))}
                    </div>
                    <div className="wf-featured-actions">
                      <button className="wf-btn">
                        <Sparkles size={16}/> Start {featured.name.split(" ").slice(-1)[0]} workflow
                      </button>
                      <button
                        type="button"
                        className="wf-btn-ghost"
                        onClick={(e) => { e.stopPropagation(); setDemoOpen(true); }}
                        data-testid="workflows-watch-demo"
                      >
                        <Play size={14}/> Watch 30-sec demo
                      </button>
                    </div>
                  </div>
                  <div
                    className="wf-featured-right wf-featured-video"
                    onClick={(e) => { e.stopPropagation(); setDemoOpen(true); }}
                    data-testid="workflows-featured-video"
                  >
                    <video
                      src="/workflows-demo.mp4"
                      poster="/workflows-demo-poster.jpg"
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="wf-featured-video-el"
                    />
                    <div className="wf-featured-play"><Play size={28} fill="white" /></div>
                  </div>
                </div>
              )}

              <h3 className="wf-section-h" data-testid="workflows-all-title">All workflows</h3>
              <div className="wf-grid" data-testid="workflows-grid">
                {rest.map((t) => {
                  const Icon = ICONS[t.icon] || Sparkles;
                  const previews = PREVIEWS[t.key] || [];
                  return (
                    <div
                      key={t.key}
                      className="wf-card"
                      data-status={t.status}
                      data-testid={`workflow-card-${t.key}`}
                      onClick={() => handleCardClick(t)}
                    >
                      <div className="wf-card-icon"><Icon /></div>
                      <h3 className="wf-card-title">{t.name}</h3>
                      <p className="wf-card-tagline">{t.tagline}</p>
                      {previews.length > 0 && (
                        <div className="wf-preview-row">
                          {previews.slice(0, 3).map((p, i) => (
                            <span key={i} className="wf-preview-chip">{p}</span>
                          ))}
                          {previews.length > 3 && <span className="wf-preview-chip wf-preview-more">+{previews.length - 3} aur</span>}
                        </div>
                      )}
                      <div className="wf-card-meta">
                        <span className={`wf-pill ${t.status === "live" ? "live" : ""}`}>
                          {t.status === "live" ? "Ready" : `Phase ${t.phase}`}
                        </span>
                        <span>{t.step_count} steps</span>
                        <span className="wf-card-cta">
                          {t.status === "live" ? <>Start <ArrowRight size={14} /></> : "Coming soon"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {demoOpen && (
        <div className="wf-demo-overlay" onClick={() => setDemoOpen(false)} data-testid="workflows-demo-modal">
          <div className="wf-demo-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="wf-demo-close"
              onClick={() => setDemoOpen(false)}
              aria-label="Close demo"
              data-testid="workflows-demo-close"
            >
              <X size={18}/>
            </button>
            <video
              src="/workflows-demo.mp4"
              poster="/workflows-demo-poster.jpg"
              autoPlay
              controls
              playsInline
              className="wf-demo-video"
            />
            <div className="wf-demo-cta">
              <p>Like what you see?</p>
              <button
                className="wf-btn"
                onClick={() => { setDemoOpen(false); featured && handleCardClick(featured); }}
              >
                <Sparkles size={14}/> Start your first workflow
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}
